import React, { KeyboardEvent, useCallback, useMemo } from 'react';
import Icon from '../../../icons/Icon';
import EditableFieldRow from '../../custom/EditableFieldRow';
import ExpandableGroup, { ExpandableGroupHeaderContext, useAccordionState } from '../../custom/ExpandableGroup';
import { AccordionIndicator, AccordionOpenCloseIcon } from '../../custom/AccordionIndicators';
import SarAccordionSectionHeader from './SarAccordionSectionHeader';
import SarValidationCategoryMapper from '../../../util/SarValidationCategoryMapper';
import { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { SarAddress, SarContactInfo, SarGercekKisi, SarIdentityDocument, SarValidationResult } from '../SarInterfaces';
import { getContactInfoTypeOptions, SarXmlTags } from '../../../constant/SarTagConstants';
import SarParser from '../SarParser';
import ADDRESS_TAGS = SarXmlTags.ADDRESS_TAGS;
import KIMLIKBELGESI_TAGS = SarXmlTags.KIMLIKBELGESI_TAGS;
import GERCEKKISI_TAGS = SarXmlTags.GERCEKKISI_TAGS;

export type { SarIdentityDocument, SarAddress, SarGercekKisi, SarContactInfo };

const GercekKisilerAccordion = ({ xmlContent, mutateXmlContent, validate }: Props) => {
  const errorsArray = validate?.errors ?? [];
  const errorsKey = errorsArray.map((e) => `${e.lineStart}-${e.lineEnd}-${e.message}`).join('|');
  const invalidFieldIds = useMemo<Set<string>>(() => {
    if (!validate || !validate.errors || validate.errors.length === 0) {
      return new Set<string>();
    }
    const fieldIds = SarValidationCategoryMapper.getInvalidFieldIds(validate.errors);
    return fieldIds;
  }, [errorsKey]);
  const gercekKisiler = useMemo(() => SarParser.parseGercekKisiler(xmlContent), [xmlContent]);
  const personErrors = useMemo<Map<number, boolean>>(() => {
    const errors = new Map<number, boolean>();
    const currentInvalidFieldIds = errorsArray.length > 0 ? SarValidationCategoryMapper.getInvalidFieldIds(errorsArray) : new Set<string>();

    gercekKisiler.forEach((_, index) => {
      const personPrefix = `gercekkisi-${index}-`;
      const personFieldErrors = Array.from(currentInvalidFieldIds).filter((fieldId) => {
        return fieldId.startsWith(personPrefix);
      });
      const hasError = personFieldErrors.length > 0;
      errors.set(index, hasError);
    });
    return errors;
  }, [gercekKisiler.length, errorsKey, errorsArray.length]);

  const accordion = useAccordionState();

  const isActivationKey = (key: string): boolean => key === 'Enter' || key === ' ';

  const handleKeyboardActivation = (event: KeyboardEvent<HTMLDivElement>, onActivate: () => void): void => {
    if (isActivationKey(event.key)) {
      event.preventDefault();
      onActivate();
    }
  };

  const updateXmlElement = ({ document, parentElement, tagName, value, shouldCreateIfNotExists = true }: UpdateXmlElementParams): void => {
    if (!parentElement) return;
    let target = parentElement.getElementsByTagName(tagName).item(0);
    if (!target && shouldCreateIfNotExists) {
      target = document.createElement(tagName);
      parentElement.appendChild(target);
    }
    if (target) {
      target.textContent = value;
    }
  };

  const getOrCreateParent = ({ document, parentElement, tagName }: GetOrCreateParentParams): Element => {
    let parent = parentElement.getElementsByTagName(tagName).item(0);
    if (!parent) {
      parent = document.createElement(tagName);
      parentElement.appendChild(parent);
    }
    return parent;
  };

  const handleFieldChange = useCallback(
    (personIndex: number, tagName: string, value: string): void => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('gercekkisi').item(personIndex);
        if (!person) return;
        updateXmlElement({ document, parentElement: person, tagName, value });
      });
    },
    [mutateXmlContent],
  );

  const handleIdentityDocumentChange = useCallback(
    (personIndex: number, documentIndex: number, tagName: string, value: string): void => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('gercekkisi').item(personIndex);
        if (!person) return;
        const documentsParent = getOrCreateParent({
          document,
          parentElement: person,
          tagName: 'kimlikbelgeleri',
        });
        const documentNode = documentsParent.getElementsByTagName('kimlikbelgesi').item(documentIndex);
        if (!documentNode) return;
        updateXmlElement({ document, parentElement: documentNode, tagName, value });
      });
    },
    [mutateXmlContent],
  );

  const handleAddressFieldChange = useCallback(
    (personIndex: number, addressIndex: number, tagName: string, value: string): void => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('gercekkisi').item(personIndex);
        if (!person) return;
        const addressesParent = getOrCreateParent({
          document,
          parentElement: person,
          tagName: 'adresler',
        });
        const addressNode = addressesParent.getElementsByTagName('adres').item(addressIndex);
        if (!addressNode) return;
        if (tagName === 'tip') {
          addressNode.setAttribute('tip', value);
          return;
        }
        updateXmlElement({ document, parentElement: addressNode, tagName, value });
      });
    },
    [mutateXmlContent],
  );

  const handleContactInfoTipChange = useCallback(
    (personIndex: number, contactIndex: number, tip: string): void => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('gercekkisi').item(personIndex);
        if (!person) return;
        const contactsParent = getOrCreateParent({
          document,
          parentElement: person,
          tagName: 'iletisimbilgileri',
        });
        const contactNode = contactsParent.getElementsByTagName('iletisimbilgisi').item(contactIndex);
        if (!contactNode) return;
        contactNode.setAttribute('tip', tip);
      });
    },
    [mutateXmlContent],
  );

  const handleContactInfoValueChange = useCallback(
    (personIndex: number, contactIndex: number, value: string): void => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('gercekkisi').item(personIndex);
        if (!person) return;
        const contactsParent = getOrCreateParent({
          document,
          parentElement: person,
          tagName: 'iletisimbilgileri',
        });
        const contactNode = contactsParent.getElementsByTagName('iletisimbilgisi').item(contactIndex);
        if (!contactNode) return;
        contactNode.textContent = value;
      });
    },
    [mutateXmlContent],
  );

  const handleAddGercekKisi = useCallback((): void => {
    mutateXmlContent((document) => {
      let gercekKisilerParent = document.getElementsByTagName('gercekkisiler').item(0);
      if (!gercekKisilerParent) {
        gercekKisilerParent = document.createElement('gercekkisiler');
        document.documentElement.appendChild(gercekKisilerParent);
      }
      const gercekKisi = document.createElement('gercekkisi');
      GERCEKKISI_TAGS.forEach((tag) => {
        const element = document.createElement(tag);
        element.textContent = '';
        gercekKisi.appendChild(element);
      });
      gercekKisi.appendChild(document.createElement('kimlikbelgeleri'));
      gercekKisi.appendChild(document.createElement('adresler'));
      gercekKisi.appendChild(document.createElement('iletisimbilgileri'));
      gercekKisilerParent.appendChild(gercekKisi);
    });
  }, [mutateXmlContent]);

  const createGroupHeaderRenderer =
    (fieldPath: string) =>
    ({ label, hasError }: ExpandableGroupHeaderContext) => <SarAccordionSectionHeader label={label} fieldPath={fieldPath} hasError={hasError} />;

  const buildInfoConfig = (path: string, type: string = 'Metin') => ({ path, type, mode: 'bubble' as const });

  const handleAddIdentityDocument = useCallback(
    (personIndex: number): void => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('gercekkisi').item(personIndex);
        if (!person) return;
        const documentsParent = getOrCreateParent({
          document,
          parentElement: person,
          tagName: 'kimlikbelgeleri',
        });
        const documentNode = document.createElement('kimlikbelgesi');
        KIMLIKBELGESI_TAGS.forEach((tag) => {
          const element = document.createElement(tag);
          element.textContent = '';
          documentNode.appendChild(element);
        });
        documentsParent.appendChild(documentNode);
      });
    },
    [mutateXmlContent],
  );

  const handleAddAddress = useCallback(
    (personIndex: number): void => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('gercekkisi').item(personIndex);
        if (!person) return;
        const addressesParent = getOrCreateParent({
          document,
          parentElement: person,
          tagName: 'adresler',
        });
        const addressNode = document.createElement('adres');
        addressNode.setAttribute('tip', '');
        ADDRESS_TAGS.forEach((tag) => {
          const element = document.createElement(tag);
          element.textContent = '';
          addressNode.appendChild(element);
        });
        addressesParent.appendChild(addressNode);
      });
    },
    [mutateXmlContent],
  );

  const handleAddContact = useCallback(
    (personIndex: number): void => {
      mutateXmlContent((document) => {
        const person = document.getElementsByTagName('gercekkisi').item(personIndex);
        if (!person) return;
        const contactsParent = getOrCreateParent({
          document,
          parentElement: person,
          tagName: 'iletisimbilgileri',
        });
        const contactNode = document.createElement('iletisimbilgisi');
        contactNode.setAttribute('tip', '');
        contactNode.textContent = '';
        contactsParent.appendChild(contactNode);
      });
    },
    [mutateXmlContent],
  );

  if (!xmlContent || xmlContent.trim().length === 0) {
    return (
      <div className={'h-full w-full p-6 flex flex-col gap-y-6'}>
        <div className={'flex items-center justify-between'}>
          <div className={'text-[#1D2939] text-base font-semibold'}>Gerçek Kişiler</div>
          <button className={'text-[11px]'} onClick={handleAddGercekKisi}>
            <Icon icon={'add'} size={14} />
          </button>
        </div>
        <div className={'rounded-xl border border-dashed border-[#D0D5DD] bg-[#F9FAFB] px-6 py-10 text-center text-sm text-[#667085]'}>
          Henüz gerçek kişi bulunmuyor.
        </div>
      </div>
    );
  }

  return (
    <div className={'h-full w-full pr-6 pt-3 2xl:pt-4 flex flex-col gap-y-3'}>
      <div className={'flex items-center justify-between'}>
        <div className={'text-[#1D2939] text-base font-semibold'}>XML Document</div>
        <button
          onClick={handleAddGercekKisi}
          type='button'
          className='w-5 h-5 hover:bg-f-gray-2 rounded-sm flex items-center justify-center transition-all duration-200'
        >
          <Icon icon='add' className='text-[#1D2939]' />
        </button>
      </div>
      {gercekKisiler.length === 0 ? (
        <div className={'rounded-xl border border-dashed border-[#D0D5DD] bg-[#F9FAFB] px-6 py-10 text-center text-sm text-[#667085]'}>
          Henüz gerçek kişi bulunmuyor.
        </div>
      ) : (
        <div className={'flex flex-col border rounded-[10px] h-full overflow-y-auto overflow-hidden'}>
          {gercekKisiler.map((person, index) => {
            const personKey = `person-${index}`;
            const expanded = accordion.isExpanded(personKey);
            const hasError = personErrors.get(index) ?? false;
            return (
              <div key={person.id} className={'bg-white rounded-b-xl rounded-t-xl'}>
                <div
                  role={'button'}
                  tabIndex={0}
                  className={`flex w-full rounded-t-xl items-center justify-between px-5 2xl:px-6 h-[56px] 2xl:h-[58px] text-left border-b ${index === gercekKisiler.length - 1 ? '' : 'border-b'}`}
                  onClick={() => accordion.toggle(personKey)}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => handleKeyboardActivation(event, () => accordion.toggle(personKey))}
                >
                  <div className='flex items-center gap-x-3'>
                    <div>
                      <AccordionOpenCloseIcon expanded={expanded} hasError={hasError} />
                    </div>
                    <div className={'flex flex-col'}>
                      <span className={`text-xs uppercase tracking-wide ${hasError ? 'text-f-red-1' : 'text-[#667085]'}`}>Information</span>
                      <span className={`text-sm font-semibold ${hasError ? 'text-f-red-1' : 'text-[#1D2939]'}`}>{`Gerçek Kişi ${index + 1}`}</span>
                    </div>
                  </div>
                  <AccordionIndicator expanded={expanded} />
                </div>
                {expanded && (
                  <div className={'flex flex-col divide-y divide-[#F2F4F7] border-b'}>
                    <EditableFieldRow
                      label={'İşlemle Bağlantısı'}
                      value={person.connectionId}
                      onChange={(value) => handleFieldChange(index, 'islemlebaglantisi', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-islemlebaglantisi`)}
                      infoConfig={buildInfoConfig('gercekKisi.islemleBaglantisi', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'İşlemle Bağlantısı Açıklama'}
                      value={person.connectionDescription}
                      onChange={(value) => handleFieldChange(index, 'islemlebaglantisiaciklama', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-islemlebaglantisiaciklama`)}
                      infoConfig={buildInfoConfig('gercekKisi.islemleBaglantisiAciklama')}
                    />
                    <EditableFieldRow
                      label={'Adı'}
                      value={person.firstName}
                      onChange={(value) => handleFieldChange(index, 'adi', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-adi`)}
                      infoConfig={buildInfoConfig('gercekKisi.adi')}
                    />
                    <EditableFieldRow
                      label={'Soyadı'}
                      value={person.lastName}
                      onChange={(value) => handleFieldChange(index, 'soyadi', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-soyadi`)}
                      infoConfig={buildInfoConfig('gercekKisi.soyadi')}
                    />
                    <EditableFieldRow
                      label={'TCKN'}
                      value={person.nationalId}
                      onChange={(value) => handleFieldChange(index, 'tckn', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-tckn`)}
                      infoConfig={buildInfoConfig('gercekKisi.tckn', 'Kimlik')}
                    />
                    <EditableFieldRow
                      label={'VKN'}
                      value={person.taxId}
                      onChange={(value) => handleFieldChange(index, 'vkn', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-vkn`)}
                      infoConfig={buildInfoConfig('gercekKisi.vkn', 'Kimlik')}
                    />
                    <EditableFieldRow
                      label={'YKN'}
                      value={person.foreignId}
                      onChange={(value) => handleFieldChange(index, 'ykn', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-ykn`)}
                      infoConfig={buildInfoConfig('gercekKisi.ykn', 'Kimlik')}
                    />
                    <EditableFieldRow
                      label={'Doğum Tarihi'}
                      value={person.birthDate}
                      onChange={(value) => handleFieldChange(index, 'dogumtarihi', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-dogumtarihi`)}
                      infoConfig={buildInfoConfig('gercekKisi.dogumTarihi', 'Tarih')}
                    />
                    <EditableFieldRow
                      label={'Doğum Yeri'}
                      value={person.birthPlace}
                      onChange={(value) => handleFieldChange(index, 'dogumyeri', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-dogumyeri`)}
                      infoConfig={buildInfoConfig('gercekKisi.dogumYeri')}
                    />
                    <EditableFieldRow
                      label={'Ana Adı'}
                      value={person.motherName}
                      onChange={(value) => handleFieldChange(index, 'anaadi', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-anaadi`)}
                      infoConfig={buildInfoConfig('gercekKisi.anaAdi')}
                    />
                    <EditableFieldRow
                      label={'Baba Adı'}
                      value={person.fatherName}
                      onChange={(value) => handleFieldChange(index, 'babaadi', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-babaadi`)}
                      infoConfig={buildInfoConfig('gercekKisi.babaAdi')}
                    />
                    <EditableFieldRow
                      label={'Uyruk'}
                      value={person.nationality}
                      onChange={(value) => handleFieldChange(index, 'uyrugu', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-uyrugu`)}
                      infoConfig={buildInfoConfig('gercekKisi.uyrugu', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Cinsiyet'}
                      value={person.gender}
                      onChange={(value) => handleFieldChange(index, 'cinsiyeti', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-cinsiyeti`)}
                      infoConfig={buildInfoConfig('gercekKisi.cinsiyeti', 'Kod')}
                    />
                    <ExpandableGroup<SarIdentityDocument>
                      baseKey={`${personKey}-identity-documents`}
                      label={'Kimlik Belgeleri'}
                      items={person.identityDocuments}
                      hasError={
                        invalidFieldIds.has(`gercekkisi-${index}-kimlikbelgeleri`) ||
                        Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`gercekkisi-${index}-kimlikbelgesi`))
                      }
                      accordion={accordion}
                      getItemTitle={(_, documentIndex) => `Kimlik Belgesi ${documentIndex + 1}`}
                      onAdd={() => handleAddIdentityDocument(index)}
                      renderHeaderDetails={createGroupHeaderRenderer('gercekKisi.kimlikBelgesi')}
                      renderItems={(identityDocument, documentIndex) => (
                        <div className={'flex flex-col rounded-b-xl divide-y divide-[#F2F4F7]'}>
                          <EditableFieldRow
                            className='pl-20 2xl:pl-28'
                            label={'Belge Türü'}
                            value={identityDocument.documentType}
                            onChange={(value) => handleIdentityDocumentChange(index, documentIndex, 'belgeturu', value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-kimlikbelgesi-${documentIndex}-belgeturu`)}
                            infoConfig={buildInfoConfig('gercekKisi.kimlikBelgesiBilgisi.belgeTuru', 'Kod')}
                          />
                          <EditableFieldRow
                            className='pl-20 2xl:pl-28'
                            label={'Belge Numarası'}
                            value={identityDocument.documentNumber}
                            onChange={(value) => handleIdentityDocumentChange(index, documentIndex, 'belgenumarasi', value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-kimlikbelgesi-${documentIndex}-belgenumarasi`)}
                            infoConfig={buildInfoConfig('gercekKisi.kimlikBelgesiBilgisi.belgeNumarasi')}
                          />
                          <EditableFieldRow
                            className='pl-20 2xl:pl-28'
                            label={'Geçerlilik Tarihi'}
                            value={identityDocument.expiryDate}
                            onChange={(value) => handleIdentityDocumentChange(index, documentIndex, 'gecerliliktarihi', value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-kimlikbelgesi-${documentIndex}-gecerliliktarihi`)}
                            infoConfig={buildInfoConfig('gercekKisi.kimlikBelgesiBilgisi.gecerlilikTarihi', 'Tarih')}
                          />
                        </div>
                      )}
                      emptyLabel={'Kimlik belgesi bulunamadı.'}
                    />
                    <EditableFieldRow
                      label={'Meslek'}
                      value={person.profession}
                      onChange={(value) => handleFieldChange(index, 'meslek', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-meslek`)}
                      infoConfig={buildInfoConfig('gercekKisi.meslek', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Ek Bilgi'}
                      value={person.additionalInfo}
                      onChange={(value) => handleFieldChange(index, 'ekbilgi', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-ekbilgi`)}
                      infoConfig={buildInfoConfig('gercekKisi.ekBilgi')}
                    />
                    <ExpandableGroup<SarAddress>
                      baseKey={`${personKey}-addresses`}
                      label={'Adresler'}
                      items={person.addresses}
                      accordion={accordion}
                      getItemTitle={(_, addressIndex) => `Adres ${addressIndex + 1}`}
                      onAdd={() => handleAddAddress(index)}
                      renderHeaderDetails={createGroupHeaderRenderer('gercekKisi.adresler')}
                      hasError={
                        invalidFieldIds.has(`gercekkisi-${index}-adresler`) ||
                        Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`gercekkisi-${index}-adres-`))
                      }
                      getItemHasError={(_, addressIndex) => {
                        return Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`gercekkisi-${index}-adres-${addressIndex}-`));
                      }}
                      renderItems={(address, addressIndex) => (
                        <div className={'flex flex-col divide-y divide-[#F2F4F7] rounded-b-xl'}>
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Adres Tipi'}
                            value={address.type}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'tip', value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-adres-${addressIndex}-tip`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres', 'Kod')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Adres Satırı'}
                            value={address.line}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'adressatir', value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-adres-${addressIndex}-adressatir`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres.adresSatir')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Posta Kodu'}
                            value={address.postalCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'postakodu', value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-adres-${addressIndex}-postakodu`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres.postaKodu')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'İl Kodu'}
                            value={address.cityCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'ilkodu', value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-adres-${addressIndex}-ilkodu`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres.ilKodu', 'Kod')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'İlçe Kodu'}
                            value={address.districtCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'ilcekodu', value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-adres-${addressIndex}-ilcekodu`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres.ilceKodu', 'Kod')}
                          />
                          <EditableFieldRow
                            className={'pl-20 2xl:pl-28'}
                            label={'Ülke Kodu'}
                            value={address.countryCode}
                            onChange={(value) => handleAddressFieldChange(index, addressIndex, 'ulkekodu', value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-adres-${addressIndex}-ulkekodu`)}
                            infoConfig={buildInfoConfig('gercekKisi.adres.ulkeKodu', 'Kod')}
                          />
                        </div>
                      )}
                      emptyLabel={'Adres bilgisi bulunamadı.'}
                    />
                    <ExpandableGroup<SarContactInfo>
                      baseKey={`${personKey}-contact`}
                      label={'İletişim Bilgileri'}
                      items={person.contactInfos}
                      accordion={accordion}
                      getItemTitle={(_, contactIndex) => `İletişim Bilgisi ${contactIndex + 1}`}
                      onAdd={() => handleAddContact(index)}
                      labelClassname={hasError ? 'text-f-red-1' : ''}
                      renderHeaderDetails={createGroupHeaderRenderer('gercekKisi.iletisimBilgileri')}
                      hasError={
                        invalidFieldIds.has(`gercekkisi-${index}-iletisimbilgileri`) ||
                        Array.from(invalidFieldIds).some((fieldId) => fieldId.startsWith(`gercekkisi-${index}-iletisimbilgisi`))
                      }
                      getItemHasError={(_, contactIndex) => {
                        return (
                          invalidFieldIds.has(`gercekkisi-${index}-iletisimbilgisi-${contactIndex}`) ||
                          invalidFieldIds.has(`gercekkisi-${index}-iletisimbilgisi-${contactIndex}-tip`)
                        );
                      }}
                      renderItems={(contact, contactIndex) => (
                        <div className={'flex flex-col divide-y divide-[#F2F4F7] rounded-b-xl'}>
                          <EditableFieldRow
                            label={'Tip'}
                            value={contact.tip}
                            onChange={(value) => handleContactInfoTipChange(index, contactIndex, value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-iletisimbilgisi-${contactIndex}-tip`)}
                            infoConfig={buildInfoConfig('gercekKisi.iletisimBilgileri.tip', 'Kod')}
                            customOptions={getContactInfoTypeOptions() as FormicaSelectBoxItem[]}
                            className={'pl-20 2xl:pl-28'}
                          />
                          <EditableFieldRow
                            label={'İletişim Bilgisi'}
                            value={contact.value}
                            className={'pl-20 2xl:pl-28'}
                            onChange={(value) => handleContactInfoValueChange(index, contactIndex, value)}
                            hasError={invalidFieldIds.has(`gercekkisi-${index}-iletisimbilgisi-${contactIndex}`)}
                          />
                        </div>
                      )}
                      emptyLabel={'İletişim bilgisi bulunamadı.'}
                    />
                    <EditableFieldRow
                      label={'Kimlik Bilgileri Sahte'}
                      value={person.fakeIdentityFlag}
                      onChange={(value) => handleFieldChange(index, 'kimlikbilgilerisahte', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-kimlikbilgilerisahte`)}
                      infoConfig={buildInfoConfig('gercekKisi.kimlikBilgileriSahte', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Mağdur'}
                      value={person.victimFlag}
                      onChange={(value) => handleFieldChange(index, 'magdur', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-magdur`)}
                      infoConfig={buildInfoConfig('gercekKisi.magdur', 'Kod')}
                    />
                    <EditableFieldRow
                      label={'Kimlik Tespit Türü'}
                      value={person.identificationType}
                      onChange={(value) => handleFieldChange(index, 'kimliktespitturu', value)}
                      hasError={invalidFieldIds.has(`gercekkisi-${index}-kimliktespitturu`)}
                      infoConfig={buildInfoConfig('gercekKisi.kimlikTespitTuruTip', 'Kod')}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GercekKisilerAccordion;

export interface UpdateXmlElementParams {
  document: Document;
  parentElement: Element | null;
  tagName: string;
  value: string;
  shouldCreateIfNotExists?: boolean;
}

interface GetOrCreateParentParams {
  document: Document;
  parentElement: Element;
  tagName: string;
}

export interface Props {
  xmlContent?: string;
  mutateXmlContent: (mutator: (document: Document) => void) => void;
  validate?: SarValidationResult;
}
